/**
 * @file `useSignInFlow` — shared state + lifecycle for SignInCard, SignInPopover, and SignInCTA.
 * @module @labcraft/ui/chrome/sign-in/use-sign-in-flow
 *
 * Owns the form lifecycle (idle → submitting → sent | error), email
 * validation, and the SSO click trampolines. All three sign-in
 * surfaces consume this hook so the canonical flow is defined in
 * exactly one place — no drift between popover, card, and inline
 * CTA.
 *
 * Decoupled from any service container or router: the caller passes
 * the three flow callbacks (`onContinueWithGithub`,
 * `onContinueWithGoogle`, `onSubmitEmail`) and the hook stays
 * brand-package-pure. Hosts wire these to their account service.
 *
 * Post-submit semantics — when `onEmailSent` is supplied, the hook
 * calls it after a successful submit and stays in `submitting` mode
 * (the caller is expected to navigate away). When omitted, the
 * hook transitions to `sent` mode so the consumer can render an
 * in-place "Check your inbox" panel. SignInPopover and SignInCTA
 * omit the callback; the lab's `/authorize` page provides it (it
 * navigates to `/auth/sent`).
 */

import { useCallback, useState, type FormEvent } from 'react'

/**
 * Loose email syntactic check. The real validation happens
 * server-side (the account service is the source of truth); this
 * just keeps the submit button enabled/disabled in the right
 * shape.
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Lifecycle stages the form can be in.
 *
 * - `idle`       — initial state. Submit button enabled iff `canSubmit`.
 * - `submitting` — request in flight. Buttons disabled, "Sending…" copy.
 * - `sent`       — request succeeded AND no `onEmailSent` callback. The
 *                  consumer renders its "Check your inbox" panel.
 * - `error`      — request rejected. Inline error banner shown; the
 *                  form is re-enabled so the user can retry.
 */
export type SignInFlowMode = 'idle' | 'submitting' | 'sent' | 'error'

/**
 * Hook input. All three entry points are individually optional —
 * each host passes only the providers it supports. www and lab wire
 * all three (GitHub + Google + Email); admin wires only Google
 * (Workspace SSO). When a callback is absent, the matching button is
 * hidden by {@link SignInCard}.
 */
export interface UseSignInFlowProps {
  /**
   * Optional same-origin post-callback redirect. Forwarded to
   * every flow; the account service sanitizes it server-side.
   */
  next?: string | null

  /** Start the GitHub OAuth round-trip. Caller navigates. */
  onContinueWithGithub?: (next?: string) => void

  /** Start the Google OIDC round-trip. Caller navigates. */
  onContinueWithGoogle?: (next?: string) => void

  /**
   * Mint a magic-link token via the account service. Returns a
   * promise — the hook awaits it and transitions to `sent` (or
   * `error`) when it settles.
   */
  onSubmitEmail?: (args: { email: string; next?: string }) => Promise<void>

  /**
   * Optional post-submit hook. When provided, replaces the hook's
   * default `sent` transition: the hook calls `onEmailSent(email)`
   * after a successful submit and stays in `submitting` mode so the
   * caller's navigation is the visible state change. Lab's
   * `/authorize` page uses this to navigate to `/auth/sent`. Popover
   * and CTA omit it so the hook handles the in-place swap.
   */
  onEmailSent?: (email: string) => void
}

/**
 * Hook output. Components destructure what they need and lay out
 * the form however they like.
 */
export interface UseSignInFlowState {
  email: string
  setEmail: (v: string) => void

  mode: SignInFlowMode
  errorMessage: string | null
  canSubmit: boolean

  /**
   * Email captured at submit time. Survives re-renders so the
   * "we sent a link to X" panel doesn't blank out if the input is
   * cleared.
   */
  sentEmail: string

  onSubmit: (e?: FormEvent) => Promise<void>
  onGithub: () => void
  onGoogle: () => void

  /**
   * Transition `sent` or `error` back to `idle`. Used by the
   * "send a new one" link inside the "Check your inbox" panel.
   */
  reset: () => void
}

/**
 * The shared sign-in state machine. See module docstring for the
 * post-submit semantics; see {@link SignInFlowMode} for the state
 * graph.
 */
export function useSignInFlow(props: UseSignInFlowProps): UseSignInFlowState {
  const next = props.next ?? null
  const [email, setEmail] = useState('')
  const [mode, setMode] = useState<SignInFlowMode>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [sentEmail, setSentEmail] = useState('')

  const canSubmit =
    Boolean(props.onSubmitEmail) &&
    mode !== 'submitting' &&
    EMAIL_REGEX.test(email.trim())

  const onSubmit = useCallback(
    async (e?: FormEvent): Promise<void> => {
      if (e) e.preventDefault()
      if (!canSubmit) return
      const submit = props.onSubmitEmail
      if (!submit) return
      const trimmed = email.trim()
      setMode('submitting')
      setErrorMessage(null)
      try {
        await submit(next ? { email: trimmed, next } : { email: trimmed })
        setSentEmail(trimmed)
        if (props.onEmailSent) {
          // Caller manages post-submit UI (e.g., navigates to /auth/sent).
          // Stay in `submitting` so buttons remain disabled until they're gone.
          props.onEmailSent(trimmed)
        } else {
          setMode('sent')
        }
      } catch (_err) {
        setErrorMessage('Something went wrong. Please try again in a moment.')
        setMode('error')
      }
    },
    [canSubmit, email, next, props],
  )

  const onGithub = useCallback(() => {
    props.onContinueWithGithub?.(next ?? undefined)
  }, [next, props])

  const onGoogle = useCallback(() => {
    props.onContinueWithGoogle?.(next ?? undefined)
  }, [next, props])

  const reset = useCallback(() => {
    setMode('idle')
    setErrorMessage(null)
  }, [])

  return {
    email,
    setEmail,
    mode,
    errorMessage,
    canSubmit,
    sentEmail,
    onSubmit,
    onGithub,
    onGoogle,
    reset,
  }
}
